import { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import Button from '../../components/ui/Button';
import ConfirmDialog from '../../components/ui/ConfirmDialog';
import Modal from '../../components/ui/Modal';
import Skeleton from '../../components/ui/Skeleton';
import { participantService } from '../../services';
import { useSession } from '../../contexts/SessionContext';
import { usePreferences } from '../../contexts/PreferencesContext';
import useWebSocket from '../../hooks/useWebSocket';
import useSimulationAutoJoin from '../../hooks/useSimulationAutoJoin';
import useMicroSimAutoJoin from '../../hooks/useMicroSimAutoJoin';
import ReconnectBanner from '../../components/participant/ReconnectBanner';
import ConnectionStatus from '../../components/session/ConnectionStatus';
import useWakeLock from '../../hooks/useWakeLock';
import tokenStorage from '../../utils/tokenStorage';
import logoFull from '../../assets/images/logo_aktivity.png';
import logoIcon from '../../assets/images/standalone_logo.png';

/**
 * Session Lobby Page (Waiting Room)
 * Clean, minimal waiting area for participants before session starts
 * Dashboard-style layout with sidebar navigation
 */

/**
 * Format duration from minutes to hours and minutes
 * @param {number} minutes - Duration in minutes
 * @returns {string} Formatted duration (e.g., "3h 5m", "1h", "45m")
 */
const formatDuration = (minutes) => {
  if (!minutes || minutes <= 0) return '0m';
  const totalHours = Math.floor(minutes / 60);
  const mins = minutes % 60;
  const days = Math.floor(totalHours / 24);
  const hours = totalHours % 24;
  if (days > 0) {
    let out = `${days}d`;
    if (hours > 0) out += ` ${hours}h`;
    if (mins > 0) out += ` ${mins}m`;
    return out;
  }
  if (totalHours === 0) return `${mins}m`;
  if (mins === 0) return `${totalHours}h`;
  return `${totalHours}h ${mins}m`;
};

const SessionLobbyPage = () => {
  const { sessionId } = useParams();
  const navigate = useNavigate();
  const { getSession, joinSession } = useSession();
  const { formatDateWithTimezone, formatTime } = usePreferences();
  // `?view=welcome` forces the lobby to open on the welcome view (phase cards) instead of the
  // default dashboard — used when the Back button from the active session page returns here.
  const forceWelcomeView = typeof window !== 'undefined'
    && new URLSearchParams(window.location.search).get('view') === 'welcome';
  const socket = useWebSocket(sessionId);

  const [session, setSession] = useState(null);
  const [participantCount, setParticipantCount] = useState(0);
  const [connectionStatus, setConnectionStatus] = useState('connecting');
  const [showNoActivityDialog, setShowNoActivityDialog] = useState(false);
  const [showSessionNotStartedDialog, setShowSessionNotStartedDialog] = useState(false);
  const [showActivityStartedDialog, setShowActivityStartedDialog] = useState(false);
  const [activityInfo, setActivityInfo] = useState(null);
  const [showOverallFeedbackDialog, setShowOverallFeedbackDialog] = useState(false);
  const [showBreakModal, setShowBreakModal] = useState(false);
  const [breakInfo, setBreakInfo] = useState({ name: '', breakTime: 0 });

  // Keep the screen awake so participants don't miss the session going live
  // or the overall-feedback launch while waiting in the lobby.
  useWakeLock(session?.status === 'scheduled' || session?.status === 'live' || session?.status === 'paused');
  const [showSubsessionStartedModal, setShowSubsessionStartedModal] = useState(false);
  const [subsessionInfo, setSubsessionInfo] = useState({ name: '', number: 0 });
  const [showSessionEndedModal, setShowSessionEndedModal] = useState(false);
  const [sessionEndReason, setSessionEndReason] = useState('');
  const [showSessionPausedModal, setShowSessionPausedModal] = useState(false);
  const [pausedCodeCopied, setPausedCodeCopied] = useState(false);
  const [isLoading, setIsLoading] = useState(true);
  const [showLeaveConfirm, setShowLeaveConfirm] = useState(false);
  const [sidebarCollapsed, setSidebarCollapsed] = useState(false);
  // Popup shown when a participant clicks a sidebar tab that's locked because
  // the session hasn't reached the right phase yet (Session / Post-Session).
  const [lockedDialog, setLockedDialog] = useState({ open: false, title: '', message: '' });
  // Popup shown when the trainer takes the session live — lets the participant opt-in to
  // navigate to the Session/Activities page instead of force-redirecting them.
  const [showSessionLiveDialog, setShowSessionLiveDialog] = useState(false);
  const [activeNavItem, setActiveNavItem] = useState('welcome'); // Start with welcome view
  const [participant, setParticipant] = useState(null);
  const [workbookViewsCount, setWorkbookViewsCount] = useState(0);

  // A trainer can launch a simulation while the room is still in the lobby — mirror
  // the activity:launched handoff so these phones move to the team screen too.
  useSimulationAutoJoin(socket, sessionId, participant?.participantId);
  // Micro simulations pair people from the lobby too — same socket, same reason.
  useMicroSimAutoJoin(socket, sessionId);

  // Load session data
  useEffect(() => {
    const fetchSession = async () => {
      setIsLoading(true);
      try {
        const response = await participantService.getSession(sessionId);
        if (response.success && response.data) {
          setSession(response.data);
          setConnectionStatus('connected');

          // Set initial participant count from session data
          const sessionParticipants = response.data.participants || [];
          const joinedCount = sessionParticipants.filter(p => p.status === 'joined').length;
          setParticipantCount(response.data.currentParticipants || joinedCount || 0);

          // Show popup if session hasn't started yet (status is 'scheduled')
          if (response.data.status === 'scheduled') {
            setShowSessionNotStartedDialog(true);
          }
        } else {
          setConnectionStatus('error');
        }

        // Get live session data from context (may have more up-to-date count)
        const liveSession = getSession(sessionId);
        if (liveSession && liveSession.participantCount > 0) {
          setParticipantCount(liveSession.participantCount);
        }

        // Fetch workbook views for this session
        try {
          const workbookViewsResponse = await participantService.getWorkbookViews(sessionId);
          if (workbookViewsResponse.success && workbookViewsResponse.data) {
            setWorkbookViewsCount(workbookViewsResponse.data.totalViewed || 0);
          }
        } catch (wbError) {
          console.log('[Lobby] Could not fetch workbook views:', wbError.message);
        }
      } catch (error) {
        console.error('Error fetching session:', error);
        setConnectionStatus('error');
      } finally {
        setIsLoading(false);
      }
    };

    fetchSession();
  }, [sessionId, getSession]);

  // Join session on mount - check for valid participant data, with cookie recovery fallback
  useEffect(() => {
    if (!sessionId) return;

    const initParticipant = async () => {
      // Get participant info from dual storage (auto-restores from sessionStorage if localStorage was purged)
      let participantData = tokenStorage.getParticipantData(sessionId);

      // If no valid participant data, try cookie recovery (Safari/iOS fallback)
      if (!participantData || (!participantData.participantId && !participantData.name)) {
        try {
          const response = await participantService.recoverSession();
          if (response.success && response.data) {
            const recovered = response.data;
            participantData = {
              participantId: recovered.participantId,
              sessionId: recovered.sessionId,
              sessionCode: recovered.sessionCode,
              name: recovered.participantName,
              email: recovered.email || '',
              organization: recovered.organization || '',
              participantCode: recovered.participantCode,
              isMultiDay: recovered.isMultiDay,
            };
            // Restore to storage
            tokenStorage.setItem('current_participant', JSON.stringify(participantData));
            tokenStorage.setItem(`participant_${recovered.sessionId}`, JSON.stringify(participantData));
          }
        } catch {
          // Cookie recovery failed — will redirect to join below
        }
      }

      // If still no valid participant data after cookie recovery, redirect to join page
      if (!participantData || (!participantData.participantId && !participantData.name)) {
        window.history.replaceState(null, '', '/join');
        navigate('/join', { replace: true });
        return;
      }

      // Set participant state
      setParticipant(participantData);

      // Ensure all storage keys are synced (covers recovery scenarios)
      tokenStorage.setParticipantData(sessionId, participantData);

      // Check if returning user - show dashboard instead of welcome.
      // `forceWelcomeView` (?view=welcome) overrides this, so the Back button from the active
      // session page always lands on the welcome view with the phase cards.
      const welcomeShownKey = `welcome_shown_${sessionId}`;
      const welcomeShown = sessionStorage.getItem(welcomeShownKey);
      if (welcomeShown && !forceWelcomeView) {
        setActiveNavItem('dashboard');
      }

      // Join session via context
      joinSession(sessionId, participantData);
    };

    initParticipant();
  }, [sessionId, joinSession, navigate]);

  // WebSocket connection status
  useEffect(() => {
    if (!socket) return;

    const handleConnect = () => {
      setConnectionStatus('connected');
    };

    const handleDisconnect = () => {
      setConnectionStatus('connecting');
    };

    socket.on('connect', handleConnect);
    socket.on('disconnect', handleDisconnect);

    if (socket.isConnected) {
      setConnectionStatus('connected');
    }

    return () => {
      socket.off('connect', handleConnect);
      socket.off('disconnect', handleDisconnect);
    };
  }, [socket]);

  // Listen for real-time updates
  useEffect(() => {
    if (!socket || !sessionId) return;

    // Participant joined (use dash format to match backend)
    const handleParticipantJoined = (data) => {
      console.log('[Lobby] Participant joined:', data);
      if (typeof data.participantCount === 'number') {
        setParticipantCount(data.participantCount);
      } else {
        setParticipantCount((prev) => prev + 1);
      }
    };

    // Participant left
    const handleParticipantLeft = (data) => {
      console.log('[Lobby] Participant left:', data);
      if (typeof data.participantCount === 'number') {
        setParticipantCount(data.participantCount);
      } else {
        setParticipantCount((prev) => Math.max(0, prev - 1));
      }
    };

    // Session status changed - update local session state
    const handleSessionStatusChanged = (data) => {
      console.log('[Lobby] Session status changed:', data);
      setSession(prev => prev ? { ...prev, status: data.status } : prev);
      if (data.status === 'live') {
        // Don't force-redirect. Show a popup so the participant knows the trainer started
        // the session and can choose to go to the Session tab. This matches the "activity
        // launched" UX pattern and avoids ripping people out of whatever page they're on.
        setShowSessionLiveDialog(true);
      } else if (data.status === 'completed' || data.status === 'ended') {
        // Session ended - navigate to reports or show message
        navigate(`/session/${sessionId}/reports`, { replace: true });
      } else if (data.status === 'paused') {
        setShowSessionPausedModal(true);
      }
    };

    // Session ended - direct event from trainer
    const handleSessionEnded = (data) => {
      console.log('[Lobby] Session ended:', data);
      setSession(prev => prev ? { ...prev, status: 'completed' } : prev);
      setSessionEndReason(data?.reason || 'The trainer has ended this session. Thank you for participating!');
      setShowSessionEndedModal(true);
    };

    // Session paused - for multi-day sessions
    const handleSessionPaused = (data) => {
      console.log('[Lobby] Session paused:', data);
      setSession(prev => prev ? { ...prev, status: 'paused' } : prev);
      setShowSessionPausedModal(true);
    };

    const handleSessionResumed = (data) => {
      console.log('[Lobby] Session resumed:', data);
      setSession(prev => prev ? { ...prev, status: 'live' } : prev);
      setShowSessionPausedModal(false);
    };

    // Activity launched (session is starting) - show dialog instead of auto-redirect
    const handleActivityLaunched = (data) => {
      console.log('[Lobby] Activity launched:', data);
      setActivityInfo(data);
      setShowActivityStartedDialog(true);
    };

    // Subsession ended - show break modal if there's a break
    const handleSubsessionEnded = (data) => {
      console.log('[Lobby] Subsession ended:', data);
      if (data?.hasBreak && data?.breakTime > 0) {
        setBreakInfo({
          name: data.name || 'Current Section',
          breakTime: data.breakTime,
        });
        setShowBreakModal(true);
      }
    };

    // Subsession started - show notification
    const handleSubsessionStarted = (data) => {
      console.log('[Lobby] Subsession started:', data);
      // Close break modal if open
      setShowBreakModal(false);
      setSubsessionInfo({
        name: data?.name || 'Next Section',
        number: data?.number || 0,
      });
      setShowSubsessionStartedModal(true);
    };

    // Overall feedback launched - show dialog
    const handleOverallFeedbackLaunched = (data) => {
      console.log('[Lobby] Overall feedback launched:', data);
      setShowOverallFeedbackDialog(true);
    };

    // Subscribe to events (use dash format to match backend)
    socket.on('participant-joined', handleParticipantJoined);
    socket.on('participant-left', handleParticipantLeft);
    socket.on('session-status-changed', handleSessionStatusChanged);
    socket.on('session:ended', handleSessionEnded);
    socket.on('session:paused', handleSessionPaused);
    socket.on('session:resumed', handleSessionResumed);
    socket.on('activity:launched', handleActivityLaunched);
    socket.on('subsession:ended', handleSubsessionEnded);
    socket.on('subsession:started', handleSubsessionStarted);
    socket.on('overallFeedback:launched', handleOverallFeedbackLaunched);

    return () => {
      socket.off('participant-joined', handleParticipantJoined);
      socket.off('participant-left', handleParticipantLeft);
      socket.off('session-status-changed', handleSessionStatusChanged);
      socket.off('session:ended', handleSessionEnded);
      socket.off('session:paused', handleSessionPaused);
      socket.off('session:resumed', handleSessionResumed);
      socket.off('activity:launched', handleActivityLaunched);
      socket.off('subsession:ended', handleSubsessionEnded);
      socket.off('subsession:started', handleSubsessionStarted);
      socket.off('overallFeedback:launched', handleOverallFeedbackLaunched);
    };
  }, [socket, sessionId, navigate]);

  // Go to dashboard from welcome view
  const goToDashboard = () => {
    setActiveNavItem('dashboard');
    // Remember that we've shown the welcome for this session
    sessionStorage.setItem(`welcome_shown_${sessionId}`, 'true');
  };

  // Go to activity from dialog
  const handleGoToActivity = () => {
    setShowActivityStartedDialog(false);
    navigate(`/session/${sessionId}/active`);
  };

  // Go to Session tab when trainer takes the session live
  const handleGoToSession = () => {
    setShowSessionLiveDialog(false);
    navigate(`/session/${sessionId}/active`);
  };

  // Go to feedback from dialog
  const handleGoToFeedback = () => {
    setShowOverallFeedbackDialog(false);
    navigate(`/session/${sessionId}/active`);
  };

  // Handle Activities card click
  const handleActivitiesClick = () => {
    // Check if session has started or has activities
    const liveSession = getSession(sessionId);
    // Activities are nested in subSessions, not directly on session
    const hasActivities = session?.subSessions?.some(sub => sub.activities && sub.activities.length > 0);
    const isSessionLive = session?.status === 'live' || liveSession?.status === 'live';

    // TODO: Uncomment this when backend is connected
    // if (!isSessionLive || !hasActivities) {
    //   setShowNoActivityDialog(true);
    // } else {
    //   navigate(`/session/${sessionId}/active`);
    // }

    // For now, always navigate to activities page (development mode)
    navigate(`/session/${sessionId}/active`);
  };

  // Navigation items for sidebar
  // Check if session is completed to show additional menu items
  const isSessionCompleted = session?.status === 'completed' || session?.status === 'ended';

  // Check if it's a multi-day session (different start and end dates)
  const isMultiDaySession = session?.startTime && session?.endTime &&
    new Date(session.startTime).toISOString().slice(0, 10) !== new Date(session.endTime).toISOString().slice(0, 10);

  // Show report/content items when session is completed OR when paused (for multi-day sessions)
  const showReportAndContent = isSessionCompleted || (session?.status === 'paused' && isMultiDaySession);

  // Phase-based navigation — lobby acts as a hub for the three session phases.
  // Click handlers route to each phase's dedicated page; Back from those pages returns here.
  //
  // Locking rules:
  //   - "Session" is locked until the session reaches `live` or `paused` (past start).
  //     When completed, it stays accessible (takes participant to active page in
  //     summary/read-only mode).
  //   - "Post-Session" is locked until the session is `completed` / `ended`.
  // Clicking a locked item opens a popup explaining why it's unavailable instead of navigating.
  const sessionStarted = session?.status === 'live' || session?.status === 'paused' || isSessionCompleted;
  // Phase 2 features (pre/post-session) are off → simplify the lobby to the
  // bare-minimum nav: Welcome + Session. There's nothing the participant is
  // waiting on outside the live window, so the Session tab also doesn't need
  // to be locked — clicking it just lands on the active session view (which
  // itself shows a "waiting for trainer" state if the session isn't live yet).
  // A pre-session tab is only worth showing if the feature is BOTH enabled AND
  // has actual content configured. A session with `preSession.enabled = true`
  // but no materials and no self-assessment questions is effectively a dead
  // tab — the participant has nothing to do there.
  //
  // Post-session is different: the tab is always meaningful when the trainer
  // enables it, regardless of whether custom reflection questions are set.
  // It still hosts action plans from the live session, the manager-reinforcement
  // code, and the default reflection cycles. Lock state below handles
  // when the tab is clickable (only after the session completes); visibility
  // itself is just "trainer turned post-session on".
  const preSessionEnabled = !!session?.preSession?.enabled;
  const postSessionEnabled = !!session?.postSession?.enabled;
  const showPreSession = preSessionEnabled && (
    (session?.preSession?.materials?.length || 0) > 0 ||
    (session?.preSession?.selfAssessment?.questions?.length || 0) > 0
  );
  const showPostSession = postSessionEnabled;
  const phase2Off = !showPreSession && !showPostSession;
  const isSessionLocked = phase2Off ? false : !sessionStarted;
  const isPostSessionLocked = !isSessionCompleted;

  const openLocked = ({ title, message }) => setLockedDialog({ open: true, title, message });

  const navigationItems = [
    {
      id: 'welcome',
      name: 'Welcome',
      icon: (
        <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24" strokeWidth={1.5}>
          <path strokeLinecap="round" strokeLinejoin="round" d="M12 6.042A8.967 8.967 0 006 3.75c-1.052 0-2.062.18-3 .512v14.25A8.987 8.987 0 016 18c2.305 0 4.408.867 6 2.292m0-14.25a8.966 8.966 0 016-2.292c1.052 0 2.062.18 3 .512v14.25A8.987 8.987 0 0018 18a8.967 8.967 0 00-6 2.292m0-14.25v14.25" />
        </svg>
      ),
      onClick: () => setActiveNavItem('welcome'),
    },
    // Pre-Session tab is hidden when the feature isn't enabled OR the
    // trainer hasn't configured any pre-session content (no materials, no
    // self-assessment questions). An "enabled but empty" pre-session is
    // treated the same as "off" for the participant view.
    showPreSession && {
      id: 'pre-session',
      name: 'Pre-Session',
      icon: (
        <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24" strokeWidth={1.5}>
          <path strokeLinecap="round" strokeLinejoin="round" d="M9 12h3.75M9 15h3.75M9 18h3.75m3 .75H18a2.25 2.25 0 002.25-2.25V6.108c0-1.135-.845-2.098-1.976-2.192a48.424 48.424 0 00-1.123-.08m-5.801 0c-.065.21-.1.433-.1.664 0 .414.336.75.75.75h4.5a.75.75 0 00.75-.75 2.25 2.25 0 00-.1-.664m-5.8 0A2.251 2.251 0 0113.5 2.25H15a2.25 2.25 0 012.15 1.586m-5.8 0c-.376.023-.75.05-1.124.08C9.095 4.01 8.25 4.973 8.25 6.108V8.25m0 0H4.875c-.621 0-1.125.504-1.125 1.125v11.25c0 .621.504 1.125 1.125 1.125h9.75c.621 0 1.125-.504 1.125-1.125V9.375c0-.621-.504-1.125-1.125-1.125H8.25z" />
        </svg>
      ),
      onClick: () => navigate(`/session/${sessionId}/pre-session`),
    },
    {
      id: 'session',
      name: 'Session',
      locked: isSessionLocked,
      icon: (
        <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24" strokeWidth={1.5}>
          <path strokeLinecap="round" strokeLinejoin="round" d="M11 4a2 2 0 114 0v1a1 1 0 001 1h3a1 1 0 011 1v3a1 1 0 01-1 1h-1a2 2 0 100 4h1a1 1 0 011 1v3a1 1 0 01-1 1h-3a1 1 0 01-1-1v-1a2 2 0 10-4 0v1a1 1 0 01-1 1H7a1 1 0 01-1-1v-3a1 1 0 00-1-1H4a2 2 0 110-4h1a1 1 0 001-1V7a1 1 0 011-1h3a1 1 0 001-1V4z" />
        </svg>
      ),
      onClick: () => {
        if (isSessionLocked) {
          openLocked({
            title: 'Session is not live yet',
            message: 'The trainer hasn\'t started the session yet. You\'ll be able to join as soon as it goes live — you\'ll also be notified automatically.',
          });
          return;
        }
        navigate(`/session/${sessionId}/active?view=welcome`);
      },
      badge: session?.status === 'live' ? 'Live' : null,
    },
    // Post-Session tab is hidden when the feature isn't enabled OR the
    // trainer hasn't configured any reflection-cycle questions. Without
    // questions there's nothing for the participant to do post-session.
    showPostSession && {
      id: 'post-session',
      name: 'Post-Session',
      locked: isPostSessionLocked,
      icon: (
        <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24" strokeWidth={1.5}>
          <path strokeLinecap="round" strokeLinejoin="round" d="M11.48 3.499a.562.562 0 011.04 0l2.125 5.111a.563.563 0 00.475.345l5.518.442c.499.04.701.663.321.988l-4.204 3.602a.563.563 0 00-.182.557l1.285 5.385a.562.562 0 01-.84.61l-4.725-2.885a.563.563 0 00-.586 0L6.982 20.54a.562.562 0 01-.84-.61l1.285-5.386a.562.562 0 00-.182-.557l-4.204-3.602a.563.563 0 01.321-.988l5.518-.442a.563.563 0 00.475-.345L11.48 3.5z" />
        </svg>
      ),
      onClick: () => {
        if (isPostSessionLocked) {
          openLocked({
            title: 'Post-Session is locked',
            message: 'This area opens once the session has ended. Check back after the trainer wraps up — you\'ll then find your feedback, action plans, and certificate here.',
          });
          return;
        }
        navigate(`/session/${sessionId}/post-session`);
      },
    },
  ].filter(Boolean);

  const bottomNavItems = [
    {
      id: 'help',
      name: 'Help Center',
      icon: (
        <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24" strokeWidth={1.5}>
          <path strokeLinecap="round" strokeLinejoin="round" d="M8.228 9c.549-1.165 2.03-2 3.772-2 2.21 0 4 1.343 4 3 0 1.4-1.278 2.575-3.006 2.907-.542.104-.994.54-.994 1.093m0 3h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
        </svg>
      ),
      onClick: () => {},
    },
  ];

  // Loading skeleton
  if (isLoading) {
    return (
      <div className="min-h-screen bg-white flex">
        {/* Sidebar Skeleton */}
        <div className="hidden lg:flex flex-col w-64 border-r border-neutral-200 p-4">
          <Skeleton variant="rectangular" width="150px" height="40px" className="mb-8" />
          <div className="space-y-2">
            {[1, 2, 3, 4, 5, 6].map((i) => (
              <Skeleton key={i} variant="rectangular" width="100%" height="40px" />
            ))}
          </div>
        </div>
        {/* Main Content Skeleton */}
        <div className="flex-1 p-6">
          <Skeleton variant="text" width="300px" height="32px" />
          <Skeleton variant="text" width="200px" className="mt-2" />
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mt-8">
            {[1, 2, 3].map((i) => (
              <Skeleton key={i} variant="rectangular" width="100%" height="120px" />
            ))}
          </div>
        </div>
      </div>
    );
  }

  // Error state
  if (connectionStatus === 'error' || !session) {
    return (
      <div className="min-h-screen bg-white flex items-center justify-center p-4">
        <div className="bg-white border border-neutral-200 rounded-xl p-8 max-w-md">
          <div className="text-center">
            <h3 className="text-lg font-semibold text-neutral-900 mb-2">Session Not Found</h3>
            <p className="text-neutral-600 mb-6">The session you're trying to join doesn't exist or has ended.</p>
            <Button onClick={() => navigate('/join')}>Back to Join</Button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-white flex">
      <ReconnectBanner isConnected={socket?.isConnected} reconnecting={socket?.reconnecting} />
      <ConnectionStatus
        isConnected={socket?.isConnected}
        reconnecting={socket?.reconnecting}
        onReconnect={() => socket?.socket?.connect?.()}
      />
      {/* Sidebar */}
      <motion.aside
        initial={false}
        animate={{ width: sidebarCollapsed ? 80 : 256 }}
        transition={{ duration: 0.3, ease: 'easeInOut' }}
        className="hidden lg:flex flex-col bg-white border-r border-neutral-200 h-screen sticky top-0"
      >
        {/* Logo */}
        <div className="p-4 border-b border-neutral-200">
          <div className="flex items-center gap-2">
            <img src={logoIcon} alt="Logo" className="h-8 w-8 object-contain" />
            <AnimatePresence>
              {!sidebarCollapsed && (
                <motion.img
                  src={logoFull}
                  alt="Aktivity"
                  className="h-8 object-contain"
                  initial={{ opacity: 0, width: 0 }}
                  animate={{ opacity: 1, width: 'auto' }}
                  exit={{ opacity: 0, width: 0 }}
                  transition={{ duration: 0.2 }}
                />
              )}
            </AnimatePresence>
          </div>
        </div>

        {/* Search */}
        {!sidebarCollapsed && (
          <div className="p-4">
            <div className="relative">
              <svg className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-neutral-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
              </svg>
              <input
                type="text"
                placeholder="Search"
                className="w-full pl-10 pr-4 py-2 bg-neutral-100 border-0 rounded-lg text-sm text-neutral-900 placeholder-neutral-500 focus:outline-none focus:ring-2 focus:ring-primary-500"
              />
            </div>
          </div>
        )}

        {/* Navigation */}
        <nav className="flex-1 overflow-y-auto py-2 px-3">
          <ul className="space-y-1">
            {navigationItems.map((item) => (
              <li key={item.id}>
                <button
                  onClick={item.onClick}
                  className={`relative w-full flex items-center gap-3 px-3 py-2.5 rounded-xl transition-all ${
                    activeNavItem === item.id
                      ? 'bg-white text-neutral-900 font-semibold ring-1 ring-inset ring-neutral-200/70 shadow-[0_1px_0_rgba(16,24,40,0.04),0_4px_12px_-4px_rgba(16,24,40,0.08)]'
                      : item.locked
                        ? 'text-neutral-400 hover:bg-neutral-50'
                        : 'text-neutral-600 hover:bg-neutral-100 hover:text-neutral-900'
                  }`}
                  title={sidebarCollapsed ? item.name : ''}
                >
                  {activeNavItem === item.id && !sidebarCollapsed && (
                    <span aria-hidden="true" className="absolute left-0 top-1/2 -translate-y-1/2 w-1 h-5 rounded-r-full bg-gradient-to-b from-primary-500 to-primary-700" />
                  )}
                  <span className={`flex-shrink-0 ${activeNavItem === item.id ? 'text-primary-600' : ''}`}>{item.icon}</span>
                  <AnimatePresence>
                    {!sidebarCollapsed && (
                      <motion.span
                        initial={{ opacity: 0, width: 0 }}
                        animate={{ opacity: 1, width: 'auto' }}
                        exit={{ opacity: 0, width: 0 }}
                        transition={{ duration: 0.2 }}
                        className="font-medium text-sm whitespace-nowrap"
                      >
                        {item.name}
                      </motion.span>
                    )}
                  </AnimatePresence>
                  {!sidebarCollapsed && item.badge && (
                    <span className="ml-auto px-2 py-0.5 bg-green-100 text-green-700 text-xs font-medium rounded-full flex items-center gap-1">
                      <span className="w-1.5 h-1.5 bg-green-500 rounded-full animate-pulse"></span>
                      {item.badge}
                    </span>
                  )}
                  {!sidebarCollapsed && item.locked && !item.badge && (
                    <span className="ml-auto text-neutral-400">
                      <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24" strokeWidth={2}>
                        <path strokeLinecap="round" strokeLinejoin="round" d="M16.5 10.5V6.75a4.5 4.5 0 10-9 0v3.75m-.75 11.25h10.5a2.25 2.25 0 002.25-2.25v-6.75a2.25 2.25 0 00-2.25-2.25H6.75a2.25 2.25 0 00-2.25 2.25v6.75a2.25 2.25 0 002.25 2.25z" />
                      </svg>
                    </span>
                  )}
                </button>
              </li>
            ))}
          </ul>
        </nav>

        {/* Bottom Navigation */}
        <div className="border-t border-neutral-200 py-2 px-3">
          <ul className="space-y-1">
            {bottomNavItems.map((item) => (
              <li key={item.id}>
                <button
                  onClick={item.onClick}
                  className="w-full flex items-center gap-3 px-3 py-2.5 rounded-xl text-neutral-700 hover:bg-neutral-100 transition-all"
                  title={sidebarCollapsed ? item.name : ''}
                >
                  <span className="flex-shrink-0">{item.icon}</span>
                  <AnimatePresence>
                    {!sidebarCollapsed && (
                      <motion.span
                        initial={{ opacity: 0, width: 0 }}
                        animate={{ opacity: 1, width: 'auto' }}
                        exit={{ opacity: 0, width: 0 }}
                        transition={{ duration: 0.2 }}
                        className="font-medium text-sm whitespace-nowrap"
                      >
                        {item.name}
                      </motion.span>
                    )}
                  </AnimatePresence>
                </button>
              </li>
            ))}
          </ul>
        </div>

        {/* Collapse Toggle */}
        <div className="p-4 border-t border-neutral-200">
          <button
            onClick={() => setSidebarCollapsed(!sidebarCollapsed)}
            className="w-full flex items-center justify-center p-2 rounded-lg text-neutral-600 hover:bg-neutral-100 transition-colors"
          >
            <svg
              className="w-5 h-5 transition-transform"
              style={{ transform: sidebarCollapsed ? 'rotate(180deg)' : 'rotate(0deg)' }}
              fill="none"
              stroke="currentColor"
              viewBox="0 0 24 24"
              strokeWidth={1.5}
            >
              <path strokeLinecap="round" strokeLinejoin="round" d="M11 19l-7-7 7-7m8 14l-7-7 7-7" />
            </svg>
          </button>
        </div>
      </motion.aside>

      {/* Main Content */}
      <div className="flex-1 flex flex-col min-h-screen">
        {/* Top Header */}
        <header className="bg-white border-b border-neutral-200 px-6 py-4 sticky top-0 z-10">
          <div className="flex items-center justify-between">
            {/* Mobile Logo */}
            <div className="lg:hidden flex items-center gap-2">
              <img src={logoIcon} alt="Logo" className="h-8 w-8 object-contain" />
              <img src={logoFull} alt="Aktivity" className="h-8 object-contain" />
            </div>

            {/* Welcome Text (Desktop) */}
            <div className="hidden lg:block">
              <h1 className="text-xl font-semibold text-neutral-900">
                Welcome, Participant
              </h1>
            </div>

            {/* Right Side Actions */}
            <div className="flex items-center gap-3">
              {/* Session Status - shows actual session status */}
              <div className={`flex items-center gap-2 px-3 py-1.5 rounded-full ${
                session?.status === 'live' ? 'bg-green-50 text-green-700' :
                session?.status === 'completed' ? 'bg-blue-50 text-blue-700' :
                session?.status === 'scheduled' ? 'bg-yellow-50 text-yellow-700' :
                'bg-neutral-50 text-neutral-700'
              }`}>
                <div className={`w-2 h-2 rounded-full ${
                  session?.status === 'live' ? 'bg-green-500 animate-pulse' :
                  session?.status === 'completed' ? 'bg-blue-500' :
                  session?.status === 'scheduled' ? 'bg-yellow-500' :
                  connectionStatus === 'connected' ? 'bg-green-500' : 'bg-yellow-500'
                }`}></div>
                <span className="text-xs font-medium hidden sm:inline">
                  {session?.status === 'live' ? 'Live' :
                   session?.status === 'completed' ? 'Completed' :
                   session?.status === 'scheduled' ? 'Scheduled' :
                   connectionStatus === 'connected' ? 'Connected' : 'Connecting...'}
                </span>
              </div>

              {/* Participant Count */}
              <div className="hidden sm:flex items-center gap-2 px-3 py-1.5 bg-neutral-100 rounded-full">
                <svg className="w-4 h-4 text-neutral-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4.354a4 4 0 110 5.292M15 21H3v-1a6 6 0 0112 0v1zm0 0h6v-1a6 6 0 00-9-5.197M13 7a4 4 0 11-8 0 4 4 0 018 0z" />
                </svg>
                <span className="text-xs font-medium text-neutral-700">{participantCount}</span>
              </div>

              {/* Leave Button */}
              <Button variant="ghost" size="sm" onClick={() => setShowLeaveConfirm(true)} className="text-neutral-600">
                Leave
              </Button>
            </div>
          </div>
        </header>

        {/* Page Content */}
        <main className="flex-1 p-4 sm:p-6 bg-white overflow-y-auto pb-24 lg:pb-6">
          {/* Welcome View - Full Session Information */}
          {activeNavItem === 'welcome' && participant && (
            <div>
              {/* Welcome Header */}
              <div className="bg-white rounded-xl border border-neutral-200 p-6 mb-6">
                <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4 mb-4">
                  <div className="flex items-center gap-4">
                    <div className="w-14 h-14 bg-green-100 rounded-xl flex items-center justify-center">
                      <svg className="w-7 h-7 text-green-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                      </svg>
                    </div>
                    <div>
                      <h1 className="text-xl sm:text-2xl font-bold text-neutral-900">
                        Welcome, {participant.name}!
                      </h1>
                      <p className="text-neutral-500 text-sm">You're all set to join this session</p>
                    </div>
                  </div>
                  <span className={`inline-flex items-center gap-2 px-3 py-1.5 text-sm font-medium rounded-full ${
                    session.status === 'live'
                      ? 'bg-green-100 text-green-700'
                      : session.status === 'scheduled'
                        ? 'bg-blue-100 text-blue-700'
                        : 'bg-neutral-100 text-neutral-700'
                  }`}>
                    <span className={`w-2 h-2 rounded-full ${
                      session.status === 'live' ? 'bg-green-500 animate-pulse' : 'bg-blue-500'
                    }`}></span>
                    {session.status === 'live' ? 'Live Session' : session.status === 'scheduled' ? 'Scheduled' : session.status}
                  </span>
                </div>

                {/* Session Info */}
                <div className="pt-4 border-t border-neutral-200">
                  <div className="flex flex-col sm:flex-row sm:items-end justify-between gap-4">
                    <div className="flex-1 min-w-0">
                      <p className="text-xs text-neutral-400 uppercase tracking-wider mb-1">Session</p>
                      <h2 className="text-lg font-bold text-neutral-900 break-words">{session.name}</h2>
                      {session.description && (
                        <p className="text-neutral-500 text-sm mt-1 break-words">{session.description}</p>
                      )}
                    </div>
                    <div className="text-right flex-shrink-0">
                      <p className="text-xs text-neutral-400 uppercase tracking-wider">Code</p>
                      <p className="text-xl font-mono font-bold text-neutral-900">#{session.code}</p>
                    </div>
                  </div>
                </div>
              </div>

              {/* Participant Code for Multi-Day Sessions */}
              {participant?.participantCode && (
                <div className="bg-purple-50 border border-purple-200 rounded-xl p-4 mb-6">
                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-3">
                      <div className="w-10 h-10 bg-purple-100 rounded-lg flex items-center justify-center flex-shrink-0">
                        <svg className="w-5 h-5 text-purple-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 7a2 2 0 012 2m4 0a6 6 0 01-7.743 5.743L11 17H9v2H7v2H4a1 1 0 01-1-1v-2.586a1 1 0 01.293-.707l5.964-5.964A6 6 0 1121 9z" />
                        </svg>
                      </div>
                      <div>
                        <p className="text-xs text-purple-600 font-medium uppercase tracking-wider">Your Participant Code</p>
                        <p className="text-xl font-mono font-bold text-purple-700">{participant.participantCode}</p>
                      </div>
                    </div>
                    <button
                      onClick={async () => {
                        try {
                          await navigator.clipboard.writeText(participant.participantCode);
                          // Brief visual feedback
                          const btn = document.getElementById('copy-code-btn');
                          if (btn) { btn.textContent = 'Copied!'; setTimeout(() => { btn.textContent = 'Copy'; }, 2000); }
                        } catch { /* ignore */ }
                      }}
                      id="copy-code-btn"
                      className="px-3 py-1.5 bg-purple-600 text-white text-sm font-medium rounded-lg hover:bg-purple-700 transition-colors"
                    >
                      Copy
                    </button>
                  </div>
                  <p className="text-xs text-purple-500 mt-2">Save this code to rejoin the session tomorrow. Use it along with session code #{session.code}</p>
                </div>
              )}

              {/* Session Details Grid - Compact on mobile */}
              <div className="grid grid-cols-2 lg:grid-cols-4 gap-2 sm:gap-4 mb-6">
                {/* Trainer */}
                <div className="bg-white rounded-lg sm:rounded-xl border border-neutral-200 p-2.5 sm:p-4 overflow-hidden">
                  <div className="flex items-center gap-2 sm:gap-3">
                    <div className="w-8 h-8 sm:w-10 sm:h-10 bg-blue-100 rounded-lg flex items-center justify-center flex-shrink-0">
                      <svg className="w-4 h-4 sm:w-5 sm:h-5 text-blue-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z" />
                      </svg>
                    </div>
                    <div className="flex-1 min-w-0 overflow-hidden">
                      <p className="text-[10px] sm:text-xs text-neutral-400 uppercase tracking-wider">Trainer</p>
                      <p className="text-xs sm:text-sm font-semibold text-neutral-900 truncate">{session.trainer?.name || 'Not assigned'}</p>
                    </div>
                  </div>
                </div>

                {/* Organization */}
                <div className="bg-white rounded-lg sm:rounded-xl border border-neutral-200 p-2.5 sm:p-4 overflow-hidden">
                  <div className="flex items-center gap-2 sm:gap-3">
                    <div className="w-8 h-8 sm:w-10 sm:h-10 bg-orange-100 rounded-lg flex items-center justify-center flex-shrink-0">
                      <svg className="w-4 h-4 sm:w-5 sm:h-5 text-orange-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 21V5a2 2 0 00-2-2H7a2 2 0 00-2 2v16m14 0h2m-2 0h-5m-9 0H3m2 0h5M9 7h1m-1 4h1m4-4h1m-1 4h1m-5 10v-5a1 1 0 011-1h2a1 1 0 011 1v5m-4 0h4" />
                      </svg>
                    </div>
                    <div className="flex-1 min-w-0 overflow-hidden">
                      <p className="text-[10px] sm:text-xs text-neutral-400 uppercase tracking-wider">Org</p>
                      <p className="text-xs sm:text-sm font-semibold text-neutral-900 truncate">{session.organization?.name || 'N/A'}</p>
                    </div>
                  </div>
                </div>

                {/* Duration - only show if set */}
                {session.duration > 0 && (
                  <div className="bg-white rounded-lg sm:rounded-xl border border-neutral-200 p-2.5 sm:p-4 overflow-hidden">
                    <div className="flex items-center gap-2 sm:gap-3">
                      <div className="w-8 h-8 sm:w-10 sm:h-10 bg-green-100 rounded-lg flex items-center justify-center flex-shrink-0">
                        <svg className="w-4 h-4 sm:w-5 sm:h-5 text-green-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" />
                        </svg>
                      </div>
                      <div className="flex-1 min-w-0 overflow-hidden">
                        <p className="text-[10px] sm:text-xs text-neutral-400 uppercase tracking-wider">Duration</p>
                        <p className="text-xs sm:text-sm font-semibold text-neutral-900">{formatDuration(session.duration)}</p>
                      </div>
                    </div>
                  </div>
                )}

                {/* Participants */}
                <div className="bg-white rounded-lg sm:rounded-xl border border-neutral-200 p-2.5 sm:p-4 overflow-hidden">
                  <div className="flex items-center gap-2 sm:gap-3">
                    <div className="w-8 h-8 sm:w-10 sm:h-10 bg-purple-100 rounded-lg flex items-center justify-center flex-shrink-0">
                      <svg className="w-4 h-4 sm:w-5 sm:h-5 text-purple-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 20h5v-2a3 3 0 00-5.356-1.857M17 20H7m10 0v-2c0-.656-.126-1.283-.356-1.857M7 20H2v-2a3 3 0 015.356-1.857M7 20v-2c0-.656.126-1.283.356-1.857m0 0a5.002 5.002 0 019.288 0M15 7a3 3 0 11-6 0 3 3 0 016 0z" />
                      </svg>
                    </div>
                    <div className="flex-1 min-w-0 overflow-hidden">
                      <p className="text-[10px] sm:text-xs text-neutral-400 uppercase tracking-wider">Online</p>
                      <p className="text-xs sm:text-sm font-semibold text-neutral-900">{participantCount}</p>
                    </div>
                  </div>
                </div>
              </div>

              {/* Session Date & Time */}
              {(session.startTime || session.scheduledStartTime) && (
                <div className="flex items-center gap-3 p-4 bg-blue-50 border border-blue-100 rounded-xl mb-6">
                  <div className="w-10 h-10 bg-blue-100 rounded-lg flex items-center justify-center flex-shrink-0">
                    <svg className="w-5 h-5 text-blue-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z" />
                    </svg>
                  </div>
                  <div>
                    <p className="text-xs text-blue-600 font-medium uppercase tracking-wider">
                      {isMultiDaySession ? 'Session Dates' : 'Session Date'}
                    </p>
                    {isMultiDaySession ? (
                      <div>
                        <p className="text-sm font-semibold text-blue-900">
                          {formatDateWithTimezone(session.startTime, { month: 'long', day: 'numeric' })} — {formatDateWithTimezone(session.endTime, { weekday: 'long', month: 'long', day: 'numeric', year: 'numeric' })}
                        </p>
                        <p className="text-xs text-blue-700 mt-0.5">
                          {formatTime(session.startTime)} - {formatTime(session.endTime)} (daily)
                        </p>
                      </div>
                    ) : (
                      <p className="text-sm font-semibold text-blue-900">
                        {formatDateWithTimezone(session.startTime || session.scheduledStartTime, { weekday: 'long', month: 'long', day: 'numeric', year: 'numeric' })}
                        {' | '}
                        {formatTime(session.startTime || session.scheduledStartTime)}
                        {session.endTime && ` - ${formatTime(session.endTime)}`}
                      </p>
                    )}
                  </div>
                </div>
              )}

              {/* Bottom Section - Tips & Status */}
              <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
                {/* Getting Started Tips */}
                <div className="bg-white rounded-xl border border-neutral-200 p-6">
                  <h3 className="text-base font-bold text-neutral-900 mb-4 flex items-center gap-2">
                    <svg className="w-5 h-5 text-primary-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9.663 17h4.673M12 3v1m6.364 1.636l-.707.707M21 12h-1M4 12H3m3.343-5.657l-.707-.707m2.828 9.9a5 5 0 117.072 0l-.548.547A3.374 3.374 0 0014 18.469V19a2 2 0 11-4 0v-.531c0-.895-.356-1.754-.988-2.386l-.548-.547z" />
                    </svg>
                    Getting Started
                  </h3>
                  <div className="space-y-3">
                    <div className="flex items-start gap-3">
                      <span className="w-6 h-6 rounded-full bg-primary-100 text-primary-600 flex items-center justify-center flex-shrink-0 text-xs font-bold">1</span>
                      <div>
                        <p className="text-sm font-medium text-neutral-900">Go to Dashboard</p>
                        <p className="text-xs text-neutral-500">Track your session progress</p>
                      </div>
                    </div>
                    <div className="flex items-start gap-3">
                      <span className="w-6 h-6 rounded-full bg-primary-100 text-primary-600 flex items-center justify-center flex-shrink-0 text-xs font-bold">2</span>
                      <div>
                        <p className="text-sm font-medium text-neutral-900">Access Workbooks</p>
                        <p className="text-xs text-neutral-500">View training materials</p>
                      </div>
                    </div>
                    <div className="flex items-start gap-3">
                      <span className="w-6 h-6 rounded-full bg-primary-100 text-primary-600 flex items-center justify-center flex-shrink-0 text-xs font-bold">3</span>
                      <div>
                        <p className="text-sm font-medium text-neutral-900">Join Activities</p>
                        <p className="text-xs text-neutral-500">Participate in polls & quizzes</p>
                      </div>
                    </div>
                  </div>
                </div>

                {/* Session Status Card */}
                <div className="bg-white rounded-xl border border-neutral-200 p-6">
                  <h3 className="text-base font-bold text-neutral-900 mb-4 flex items-center gap-2">
                    <svg className={`w-5 h-5 ${session.status === 'live' ? 'text-green-600' : 'text-blue-600'}`} fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
                    </svg>
                    Session Status
                  </h3>
                  <div className="space-y-4">
                    <div className="flex items-center justify-between">
                      <span className="text-sm text-neutral-500">Status</span>
                      <span className={`flex items-center gap-2 text-sm font-medium ${
                        session.status === 'live' ? 'text-green-600' : 'text-blue-600'
                      }`}>
                        <span className={`w-2 h-2 rounded-full ${
                          session.status === 'live' ? 'bg-green-500 animate-pulse' : 'bg-blue-500'
                        }`}></span>
                        {session.status === 'live' ? 'Active' : session.status === 'scheduled' ? 'Waiting to Start' : session.status}
                      </span>
                    </div>
                    <div className="flex items-center justify-between">
                      <span className="text-sm text-neutral-500">Participants</span>
                      <span className="text-sm font-medium text-neutral-900">{participantCount} online</span>
                    </div>
                    <div className="flex items-center justify-between">
                      <span className="text-sm text-neutral-500">Activities</span>
                      <span className="text-sm font-medium text-neutral-900">
                        {(() => {
                          const totalActivities = session.subSessions?.reduce((sum, ss) => sum + (ss.activities?.length || 0), 0) || 0;
                          const launchedActivities = session.subSessions?.reduce((sum, ss) =>
                            sum + (ss.activities?.filter(a => a.status === 'launched' || a.status === 'completed').length || 0), 0) || 0;
                          if (session.status === 'completed') return 'Session completed';
                          if (session.status === 'paused') return `${launchedActivities}/${totalActivities} completed`;
                          if (totalActivities === 0) return 'No activities yet';
                          if (launchedActivities > 0) return `${launchedActivities}/${totalActivities} launched`;
                          return 'Waiting to start';
                        })()}
                      </span>
                    </div>
                    <div className="pt-3 border-t border-neutral-100">
                      <p className="text-xs text-neutral-400 text-center">
                        {session.status === 'live'
                          ? 'Stay on this page to receive updates when activities start'
                          : 'The trainer will start the session soon. Please wait here.'}
                      </p>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          )}

          {/* Dashboard View - Original Dashboard Content */}
          {activeNavItem === 'dashboard' && (
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
            >
              {/* Greeting Header */}
          <div className="flex flex-col sm:flex-row sm:items-start sm:justify-between gap-3 mb-6">
            {/* Left Side - Greeting */}
            <div>
              <h1 className="text-xl sm:text-2xl font-bold text-neutral-900">
                {(() => {
                  const hour = new Date().getHours();
                  if (hour < 12) return 'Good Morning!';
                  if (hour < 17) return 'Good Afternoon!';
                  return 'Good Evening!';
                })()}
              </h1>
              <p className="text-sm sm:text-base text-neutral-500 mt-1">Welcome to <span className="font-medium text-neutral-700">{session.name}</span></p>
            </div>
            {/* Right Side - Date & Code */}
            <div className="sm:text-right flex sm:flex-col items-center sm:items-end gap-2 sm:gap-0">
              <p className="text-xs sm:text-sm text-neutral-500 hidden sm:block">
                {new Date().toLocaleDateString('en-US', { weekday: 'long', day: 'numeric', month: 'long' })}
              </p>
              <div className="inline-flex items-center gap-2 sm:mt-1 px-3 py-1.5 bg-neutral-100 rounded-full">
                <span className="text-xs text-neutral-500">Code:</span>
                <span className="text-xs font-mono font-semibold text-neutral-900">#{session.code}</span>
              </div>
            </div>
          </div>

          {/* Stats Cards - Compact Style */}
          <div className="grid grid-cols-3 gap-2 sm:gap-3 mb-6">
            {/* Participants Card */}
            <div className="bg-white rounded-xl border border-neutral-200 p-3 sm:p-4 text-center hover:shadow-sm transition-shadow">
              <div className="w-8 h-8 sm:w-9 sm:h-9 bg-green-50 rounded-lg flex items-center justify-center mx-auto mb-1.5 sm:mb-2">
                <svg className="w-5 h-5 text-green-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 20h5v-2a3 3 0 00-5.356-1.857M17 20H7m10 0v-2c0-.656-.126-1.283-.356-1.857M7 20H2v-2a3 3 0 015.356-1.857M7 20v-2c0-.656.126-1.283.356-1.857m0 0a5.002 5.002 0 019.288 0M15 7a3 3 0 11-6 0 3 3 0 016 0zm6 3a2 2 0 11-4 0 2 2 0 014 0zM7 10a2 2 0 11-4 0 2 2 0 014 0z" />
                </svg>
              </div>
              <p className="text-[10px] text-neutral-500 uppercase tracking-wide mb-0.5">Participants</p>
              <p className="text-2xl font-bold text-neutral-900">{participantCount}</p>
              <p className="text-xs text-green-600 flex items-center justify-center gap-1">
                <span className="w-1 h-1 bg-green-500 rounded-full animate-pulse"></span>
                Active now
              </p>
            </div>

            {/* Duration Card - only show if set */}
            {session.duration > 0 && (
              <div className="bg-white rounded-xl border border-neutral-200 p-3 sm:p-4 text-center hover:shadow-sm transition-shadow">
                <div className="w-8 h-8 sm:w-9 sm:h-9 bg-blue-50 rounded-lg flex items-center justify-center mx-auto mb-1.5 sm:mb-2">
                  <svg className="w-5 h-5 text-blue-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" />
                  </svg>
                </div>
                <p className="text-[10px] text-neutral-500 uppercase tracking-wide mb-0.5">Duration</p>
                <p className="text-xl sm:text-2xl font-bold text-neutral-900">{formatDuration(session.duration)}</p>
              </div>
            )}

            {/* Organization Card */}
            <div className="bg-white rounded-xl border border-neutral-200 p-3 sm:p-4 text-center hover:shadow-sm transition-shadow">
              <div className="w-8 h-8 sm:w-9 sm:h-9 bg-purple-50 rounded-lg flex items-center justify-center mx-auto mb-1.5 sm:mb-2">
                <svg className="w-5 h-5 text-purple-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 21V5a2 2 0 00-2-2H7a2 2 0 00-2 2v16m14 0h2m-2 0h-5m-9 0H3m2 0h5M9 7h1m-1 4h1m4-4h1m-1 4h1m-5 10v-5a1 1 0 011-1h2a1 1 0 011 1v5m-4 0h4" />
                </svg>
              </div>
              <p className="text-[10px] text-neutral-500 uppercase tracking-wide mb-0.5">Organization</p>
              <p className="text-base sm:text-lg font-bold text-neutral-900 truncate">{session.organization?.name || 'N/A'}</p>
              <p className="text-xs text-neutral-500">Host</p>
            </div>
          </div>

          {/* Progress Section */}
          {(() => {
            // Calculate progress metrics from session data
            const totalActivities = session.subSessions?.reduce((sum, ss) => sum + (ss.activities?.length || 0), 0) || 0;
            // Count of currently-launched activities — kept for the trainer-
            // side detail view but no longer used as the completion denominator
            // (we switched to the whole-session total to match the
            // ParticipantReportsPage convention so progress reads consistently
            // across the app).
            const launchedActivities = session.subSessions?.reduce((sum, ss) =>
              sum + (ss.activities?.filter(a =>
                a.status === 'active' || a.status === 'completed' || a.status === 'closed' ||
                a.launchedAt || a.isLaunched || (a.responses && a.responses.length > 0)
              ).length || 0), 0) || 0;
            // Dedupe responses by activity id — a relaunched activity or a
            // multi-stage submission can produce multiple userResponses entries
            // for the same activity, which would otherwise let the numerator
            // exceed the denominator and produce >100% completion.
            const respondedActivityIds = new Set(
              (session.userResponses || [])
                .map(r => (r.activity || r.activityId || r._id)?.toString())
                .filter(Boolean)
            );
            const respondedActivities = Math.min(respondedActivityIds.size, totalActivities);
            // Denominator is the whole session's activity count, matching how
            // ParticipantReportsPage reports completion. Cap at 100% as a
            // defensive backstop in case the dedupe above misses anything
            // (e.g. responses with no activity reference).
            const activitiesCompletedPercent = totalActivities > 0
              ? Math.min(100, Math.round((respondedActivities / totalActivities) * 100))
              : 0;

            // Calculate quizzes passed - check multiple possible score locations
            const quizActivities = session.subSessions?.flatMap(ss =>
              ss.activities?.filter(a => a.type?.toLowerCase() === 'quiz') || []
            ) || [];
            const totalQuizzes = quizActivities.length;
            const quizzesPassed = session.userResponses?.filter(r => {
              if (r.activityType?.toLowerCase() !== 'quiz') return false;
              // Check all possible locations for quiz score
              const score = r.response?.results?.percentage ||
                           r.response?.score ||
                           r.score ||
                           0;
              return score >= 70; // Passing score threshold
            }).length || 0;
            const quizzesPassedPercent = totalQuizzes > 0
              ? Math.min(100, Math.round((quizzesPassed / totalQuizzes) * 100))
              : 0;

            // Content viewed (workbooks) - use actual tracking data
            const totalWorkbooks = session.workbooks?.length || 0;
            const contentViewedPercent = totalWorkbooks > 0
              ? Math.min(100, Math.round((workbookViewsCount / totalWorkbooks) * 100))
              : 0;

            return (
              <div className="grid grid-cols-1 lg:grid-cols-3 gap-4 sm:gap-6 mb-6 sm:mb-8">
                {/* Activity Progress */}
                <div className="bg-white rounded-xl sm:rounded-2xl border border-neutral-200 p-4 sm:p-6">
                  <div className="flex items-center justify-between mb-4 sm:mb-6">
                    <h3 className="text-base sm:text-lg font-semibold text-neutral-900">Session Progress</h3>
                    <span className="text-xs sm:text-sm text-neutral-500">Today</span>
                  </div>

                  {/* Progress Items */}
                  <div className="space-y-3 sm:space-y-4">
                    <div className="flex items-center gap-3 sm:gap-4">
                      <div className="w-8 h-8 sm:w-10 sm:h-10 bg-blue-100 rounded-lg flex items-center justify-center flex-shrink-0">
                        <svg className="w-4 h-4 sm:w-5 sm:h-5 text-blue-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5H7a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2" />
                        </svg>
                      </div>
                      <div className="flex-1">
                        <div className="flex items-center justify-between mb-1">
                          <span className="text-xs sm:text-sm font-medium text-neutral-900">Content Viewed</span>
                          <span className="text-xs sm:text-sm text-neutral-500">{contentViewedPercent}%</span>
                        </div>
                        <div className="h-1.5 sm:h-2 bg-neutral-100 rounded-full overflow-hidden">
                          <div className="h-full bg-blue-500 rounded-full transition-all duration-500" style={{ width: `${contentViewedPercent}%` }}></div>
                        </div>
                      </div>
                    </div>

                    <div className="flex items-center gap-3 sm:gap-4">
                      <div className="w-8 h-8 sm:w-10 sm:h-10 bg-orange-100 rounded-lg flex items-center justify-center flex-shrink-0">
                        <svg className="w-4 h-4 sm:w-5 sm:h-5 text-orange-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M11 4a2 2 0 114 0v1a1 1 0 001 1h3a1 1 0 011 1v3a1 1 0 01-1 1h-1a2 2 0 100 4h1a1 1 0 011 1v3a1 1 0 01-1 1h-3a1 1 0 01-1-1v-1a2 2 0 10-4 0v1a1 1 0 01-1 1H7a1 1 0 01-1-1v-3a1 1 0 00-1-1H4a2 2 0 110-4h1a1 1 0 001-1V7a1 1 0 011-1h3a1 1 0 001-1V4z" />
                        </svg>
                      </div>
                      <div className="flex-1">
                        <div className="flex items-center justify-between mb-1">
                          <span className="text-xs sm:text-sm font-medium text-neutral-900">Activities Completed</span>
                          <span className="text-xs sm:text-sm text-neutral-500">{activitiesCompletedPercent}%</span>
                        </div>
                        <div className="h-1.5 sm:h-2 bg-neutral-100 rounded-full overflow-hidden">
                          <div className="h-full bg-orange-500 rounded-full transition-all duration-500" style={{ width: `${activitiesCompletedPercent}%` }}></div>
                        </div>
                      </div>
                    </div>

                    <div className="flex items-center gap-3 sm:gap-4">
                      <div className="w-8 h-8 sm:w-10 sm:h-10 bg-green-100 rounded-lg flex items-center justify-center flex-shrink-0">
                        <svg className="w-4 h-4 sm:w-5 sm:h-5 text-green-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
                        </svg>
                      </div>
                      <div className="flex-1">
                        <div className="flex items-center justify-between mb-1">
                          <span className="text-xs sm:text-sm font-medium text-neutral-900">Quizzes Passed</span>
                          <span className="text-xs sm:text-sm text-neutral-500">{quizzesPassedPercent}%</span>
                        </div>
                        <div className="h-1.5 sm:h-2 bg-neutral-100 rounded-full overflow-hidden">
                          <div className="h-full bg-green-500 rounded-full transition-all duration-500" style={{ width: `${quizzesPassedPercent}%` }}></div>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>

                {/* Session Status Ring */}
                <div className="bg-white rounded-xl sm:rounded-2xl border border-neutral-200 p-4 sm:p-6">
                  <div className="flex items-center justify-between mb-4 sm:mb-6">
                    <h3 className="text-base sm:text-lg font-semibold text-neutral-900">Session Status</h3>
                    <span className={`flex items-center gap-1.5 px-2 py-1 text-xs font-medium rounded-full ${
                      session.status === 'live' ? 'bg-green-100 text-green-700' : 'bg-blue-100 text-blue-700'
                    }`}>
                      <span className={`w-1.5 h-1.5 rounded-full ${session.status === 'live' ? 'bg-green-500 animate-pulse' : 'bg-blue-500'}`}></span>
                      {session.status === 'live' ? 'Live' : session.status === 'scheduled' ? 'Scheduled' : session.status}
                    </span>
                  </div>

                  <div className="flex items-center justify-center py-2 sm:py-4">
                    {/* Circular Progress */}
                    <div className="relative w-28 h-28 sm:w-32 sm:h-32">
                      <svg className="w-full h-full transform -rotate-90" viewBox="0 0 160 160">
                        <circle
                          cx="80"
                          cy="80"
                          r="70"
                          fill="none"
                          stroke="#f3f4f6"
                          strokeWidth="12"
                        />
                        <circle
                          cx="80"
                          cy="80"
                          r="70"
                          fill="none"
                          stroke="#3b82f6"
                          strokeWidth="12"
                          strokeLinecap="round"
                          strokeDasharray={`${activitiesCompletedPercent * 4.4} 440`}
                        />
                      </svg>
                      <div className="absolute inset-0 flex flex-col items-center justify-center">
                        <span className="text-2xl sm:text-3xl font-bold text-neutral-900">{respondedActivities}</span>
                        <span className="text-[10px] sm:text-xs text-neutral-500 uppercase tracking-wide">of {totalActivities}</span>
                        <span className="text-[10px] sm:text-xs text-neutral-400">Activities</span>
                      </div>
                    </div>
                  </div>

                  {/* Legend */}
                  <div className="flex items-center justify-center gap-4 sm:gap-6 mt-2 sm:mt-4">
                    <div className="flex items-center gap-1.5 sm:gap-2">
                      <span className="w-2.5 h-2.5 sm:w-3 sm:h-3 bg-blue-500 rounded-full"></span>
                      <span className="text-xs sm:text-sm text-neutral-600">Completed</span>
                    </div>
                    <div className="flex items-center gap-1.5 sm:gap-2">
                      <span className="w-2.5 h-2.5 sm:w-3 sm:h-3 bg-neutral-200 rounded-full"></span>
                      <span className="text-xs sm:text-sm text-neutral-600">Pending</span>
                    </div>
                  </div>
                </div>

            {/* Engagement Score */}
                {(() => {
                  const engagementScore = activitiesCompletedPercent; // Use activities completed as engagement score
                  const scoreLabel = engagementScore >= 80 ? 'Excellent' : engagementScore >= 60 ? 'Good' : engagementScore >= 40 ? 'Average' : engagementScore > 0 ? 'Low' : 'N/A';
                  const scoreColor = engagementScore >= 60 ? 'text-green-600' : engagementScore >= 40 ? 'text-yellow-600' : 'text-neutral-400';

                  return (
                    <div className="bg-white rounded-xl sm:rounded-2xl border border-neutral-200 p-4 sm:p-6">
                      <div className="flex items-center justify-between mb-4 sm:mb-6">
                        <h3 className="text-base sm:text-lg font-semibold text-neutral-900">Engagement Score</h3>
                      </div>

                      <div className="flex items-center justify-center py-2 sm:py-4">
                        {/* Engagement Score Ring */}
                        <div className="relative w-28 h-28 sm:w-32 sm:h-32">
                          <svg className="w-full h-full transform -rotate-90" viewBox="0 0 160 160">
                            <circle
                              cx="80"
                              cy="80"
                              r="70"
                              fill="none"
                              stroke="#f3f4f6"
                              strokeWidth="12"
                            />
                            <circle
                              cx="80"
                              cy="80"
                              r="70"
                              fill="none"
                              stroke="#ff8a00"
                              strokeWidth="12"
                              strokeLinecap="round"
                              strokeDasharray={`${engagementScore * 4.4} 440`}
                            />
                          </svg>
                          <div className="absolute inset-0 flex flex-col items-center justify-center">
                            <span className="text-2xl sm:text-3xl font-bold text-neutral-900">{engagementScore}</span>
                            <span className="text-[10px] sm:text-xs text-neutral-500 uppercase tracking-wide">Score</span>
                            <span className={`text-[10px] sm:text-xs font-medium ${scoreColor}`}>{scoreLabel}</span>
                          </div>
                        </div>
                      </div>

                      {/* Engagement Metrics */}
                      <div className="space-y-2 mt-2 sm:mt-4">
                        <div className="flex items-center justify-between text-xs sm:text-sm">
                          <span className="text-neutral-500 flex items-center gap-1.5">
                            <span className="w-2 h-2 bg-orange-500 rounded-full"></span>
                            Activities Responded
                          </span>
                          <span className="font-medium text-neutral-900">{respondedActivities}/{totalActivities}</span>
                        </div>
                        <div className="flex items-center justify-between text-xs sm:text-sm">
                          <span className="text-neutral-500 flex items-center gap-1.5">
                            <span className="w-2 h-2 bg-blue-500 rounded-full"></span>
                            Response Rate
                          </span>
                          <span className="font-medium text-neutral-900">{activitiesCompletedPercent}%</span>
                        </div>
                        <div className="flex items-center justify-between text-xs sm:text-sm">
                          <span className="text-neutral-500 flex items-center gap-1.5">
                            <span className="w-2 h-2 bg-green-500 rounded-full"></span>
                            Session Status
                          </span>
                          <span className="font-medium text-neutral-900 capitalize">{session.status}</span>
                        </div>
                      </div>
                    </div>
                  );
                })()}
              </div>
            );
          })()}

          {/* Quick Actions */}
          <div className="mb-6">
            <h3 className="text-base sm:text-lg font-semibold text-neutral-900 mb-3 sm:mb-4">Quick Actions</h3>
            <div className="grid grid-cols-2 md:grid-cols-4 gap-3 sm:gap-4">
              {/* Content */}
              <button
                onClick={() => navigate(`/session/${sessionId}/workbooks`)}
                className="bg-white rounded-xl sm:rounded-2xl border border-neutral-200 p-3 sm:p-5 hover:border-blue-300 hover:shadow-md transition-all group text-left"
              >
                <div className="w-10 h-10 sm:w-12 sm:h-12 bg-blue-50 rounded-lg sm:rounded-xl flex items-center justify-center mb-2 sm:mb-3 group-hover:bg-blue-100 transition-colors">
                  <svg className="w-5 h-5 sm:w-6 sm:h-6 text-blue-600" fill="none" stroke="currentColor" viewBox="0 0 24 24" strokeWidth={1.5}>
                    <path strokeLinecap="round" strokeLinejoin="round" d="M9 5H7a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2" />
                  </svg>
                </div>
                <h4 className="text-sm sm:text-base font-semibold text-neutral-900 mb-0.5 sm:mb-1">Content</h4>
                <p className="text-xs sm:text-sm text-neutral-500 hidden sm:block">Session details</p>
              </button>

              {/* Workbooks */}
              <button
                onClick={() => navigate(`/session/${sessionId}/launched-workbooks`)}
                className="bg-white rounded-xl sm:rounded-2xl border border-neutral-200 p-3 sm:p-5 hover:border-indigo-300 hover:shadow-md transition-all group text-left"
              >
                <div className="w-10 h-10 sm:w-12 sm:h-12 bg-indigo-50 rounded-lg sm:rounded-xl flex items-center justify-center mb-2 sm:mb-3 group-hover:bg-indigo-100 transition-colors">
                  <svg className="w-5 h-5 sm:w-6 sm:h-6 text-indigo-600" fill="none" stroke="currentColor" viewBox="0 0 24 24" strokeWidth={1.5}>
                    <path strokeLinecap="round" strokeLinejoin="round" d="M12 6.253v13m0-13C10.832 5.477 9.246 5 7.5 5S4.168 5.477 3 6.253v13C4.168 18.477 5.754 18 7.5 18s3.332.477 4.5 1.253m0-13C13.168 5.477 14.754 5 16.5 5c1.747 0 3.332.477 4.5 1.253v13C19.832 18.477 18.247 18 16.5 18c-1.746 0-3.332.477-4.5 1.253" />
                  </svg>
                </div>
                <h4 className="text-sm sm:text-base font-semibold text-neutral-900 mb-0.5 sm:mb-1">Workbooks</h4>
                <p className="text-xs sm:text-sm text-neutral-500 hidden sm:block">Training materials</p>
              </button>

              {/* Activities */}
              <button
                onClick={handleActivitiesClick}
                className="bg-white rounded-xl sm:rounded-2xl border border-neutral-200 p-3 sm:p-5 hover:border-orange-300 hover:shadow-md transition-all group text-left relative"
              >
                {session?.status === 'live' && (
                  <div className="absolute top-2 right-2 sm:top-3 sm:right-3">
                    <span className="flex items-center gap-1 px-1.5 sm:px-2 py-0.5 bg-green-100 text-green-700 text-[10px] sm:text-xs font-medium rounded-full">
                      <span className="w-1 h-1 sm:w-1.5 sm:h-1.5 bg-green-500 rounded-full animate-pulse"></span>
                      Live
                    </span>
                  </div>
                )}
                <div className="w-10 h-10 sm:w-12 sm:h-12 bg-orange-50 rounded-lg sm:rounded-xl flex items-center justify-center mb-2 sm:mb-3 group-hover:bg-orange-100 transition-colors">
                  <svg className="w-5 h-5 sm:w-6 sm:h-6 text-orange-600" fill="none" stroke="currentColor" viewBox="0 0 24 24" strokeWidth={1.5}>
                    <path strokeLinecap="round" strokeLinejoin="round" d="M11 4a2 2 0 114 0v1a1 1 0 001 1h3a1 1 0 011 1v3a1 1 0 01-1 1h-1a2 2 0 100 4h1a1 1 0 011 1v3a1 1 0 01-1 1h-3a1 1 0 01-1-1v-1a2 2 0 10-4 0v1a1 1 0 01-1 1H7a1 1 0 01-1-1v-3a1 1 0 00-1-1H4a2 2 0 110-4h1a1 1 0 001-1V7a1 1 0 011-1h3a1 1 0 001-1V4z" />
                  </svg>
                </div>
                <h4 className="text-sm sm:text-base font-semibold text-neutral-900 mb-0.5 sm:mb-1">Aktivities</h4>
                <p className="text-xs sm:text-sm text-neutral-500 hidden sm:block">Polls & quizzes</p>
              </button>

              {/* My Workspace */}
              <button
                onClick={() => navigate(`/session/${sessionId}/notes`)}
                className="bg-white rounded-xl sm:rounded-2xl border border-neutral-200 p-3 sm:p-5 hover:border-purple-300 hover:shadow-md transition-all group text-left"
              >
                <div className="w-10 h-10 sm:w-12 sm:h-12 bg-purple-50 rounded-lg sm:rounded-xl flex items-center justify-center mb-2 sm:mb-3 group-hover:bg-purple-100 transition-colors">
                  <svg className="w-5 h-5 sm:w-6 sm:h-6 text-purple-600" fill="none" stroke="currentColor" viewBox="0 0 24 24" strokeWidth={1.5}>
                    <path strokeLinecap="round" strokeLinejoin="round" d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z" />
                  </svg>
                </div>
                <h4 className="text-sm sm:text-base font-semibold text-neutral-900 mb-0.5 sm:mb-1">Workspace</h4>
                <p className="text-xs sm:text-sm text-neutral-500 hidden sm:block">Take notes</p>
              </button>
            </div>
          </div>
            </motion.div>
          )}
        </main>
      </div>

      {/* Mobile Bottom Navigation - Show all items with smaller sizes */}
      <div className="lg:hidden fixed bottom-0 left-0 right-0 bg-white border-t border-neutral-200 z-40 safe-area-inset-bottom">
        <nav className="flex justify-around items-center h-14 px-0.5">
          {/* Show all navigation items with compact styling */}
          {navigationItems.map((item) => (
            <button
              key={item.id}
              onClick={item.onClick}
              className={`flex flex-col items-center justify-center gap-0 px-0.5 py-1 flex-1 min-w-0 transition-colors ${
                activeNavItem === item.id ? '' : item.locked ? 'text-neutral-300' : 'text-neutral-500'
              }`}
              style={activeNavItem === item.id ? { color: '#ff8a00' } : {}}
            >
              <span className="relative [&>svg]:w-4 [&>svg]:h-4">
                {item.icon}
                {item.badge && (
                  <span className="absolute -top-0.5 -right-0.5 w-1.5 h-1.5 bg-green-500 rounded-full animate-pulse"></span>
                )}
                {item.locked && !item.badge && (
                  <span className="absolute -top-1 -right-1 bg-white rounded-full p-[1px]">
                    <svg className="w-2.5 h-2.5 text-neutral-400" fill="currentColor" viewBox="0 0 24 24">
                      <path d="M17 8h-1V6a4 4 0 10-8 0v2H7a2 2 0 00-2 2v10a2 2 0 002 2h10a2 2 0 002-2V10a2 2 0 00-2-2zM10 6a2 2 0 114 0v2h-4V6z" />
                    </svg>
                  </span>
                )}
              </span>
              <span className="text-[8px] font-medium truncate w-full text-center leading-tight mt-0.5">
                {item.id === 'activities' ? 'Aktivity' : item.id === 'workspace' ? 'Notes' : item.name}
              </span>
            </button>
          ))}
        </nav>
      </div>

      {/* Leave Session Confirmation */}
      <ConfirmDialog
        isOpen={showLeaveConfirm}
        onClose={() => setShowLeaveConfirm(false)}
        onConfirm={() => {
          // Emit leave-session to socket for accurate participant count
          if (socket) {
            socket.emit('leave-session', sessionId);
          }

          // Clear participant data from localStorage
          participantService.clearParticipantData();

          // Clear session-specific data from sessionStorage
          sessionStorage.removeItem(`welcome_shown_${sessionId}`);
          sessionStorage.clear();

          // Replace history to prevent back navigation
          window.history.replaceState(null, '', '/join');
          navigate('/join', { replace: true });
        }}
        title="Leave Session"
        message="Are you sure you want to leave this session? You can rejoin later using the session code."
        confirmText="Leave"
        cancelText="Stay"
        variant="warning"
      />

      {/* No Activity Dialog */}
      <ConfirmDialog
        isOpen={showNoActivityDialog}
        onClose={() => setShowNoActivityDialog(false)}
        onConfirm={() => setShowNoActivityDialog(false)}
        title="Session Not Started Yet"
        message="The trainer hasn't started any activities yet. Please wait for the session to begin."
        confirmText="OK"
        cancelText="Cancel"
        variant="info"
      />

      {/* Session Not Started Dialog - shown when participant joins before session starts */}
      <ConfirmDialog
        isOpen={showSessionNotStartedDialog}
        onClose={() => setShowSessionNotStartedDialog(false)}
        onConfirm={() => setShowSessionNotStartedDialog(false)}
        title="Session Not Started Yet"
        message="The session hasn't started yet. It will begin once the trainer joins and starts the session. Please wait here."
        confirmText="OK, I'll Wait"
        cancelText=""
        variant="info"
      />

      {/* Activity Started Dialog */}
      <ConfirmDialog
        isOpen={showActivityStartedDialog}
        onClose={() => setShowActivityStartedDialog(false)}
        onConfirm={handleGoToActivity}
        title={`${activityInfo?.activityType === 'poll' ? 'Poll' : activityInfo?.activityType === 'quiz' ? 'Quiz' : 'Aktivity'} Started!`}
        message={`${activityInfo?.activityTitle || 'An aktivity'} has been launched. Would you like to participate?`}
        confirmText="Go to Aktivity"
        cancelText="Stay Here"
        variant="info"
      />

      {/* Session Live Dialog — shown when the trainer starts the session */}
      <ConfirmDialog
        isOpen={showSessionLiveDialog}
        onClose={() => setShowSessionLiveDialog(false)}
        onConfirm={handleGoToSession}
        title="Session is Live!"
        message="Your trainer has started the session. Go to the Session tab to join the activities."
        confirmText="Go to Session"
        cancelText="Stay Here"
        variant="success"
      />

      {/* Overall Feedback Dialog */}
      <ConfirmDialog
        isOpen={showOverallFeedbackDialog}
        onClose={() => setShowOverallFeedbackDialog(false)}
        onConfirm={handleGoToFeedback}
        title="Session Feedback Requested"
        message="The trainer has requested your feedback on this session. Please take a moment to share your thoughts."
        confirmText="Give Feedback"
        cancelText="Later"
        variant="info"
      />

      {/* Break Time Modal */}
      <Modal
        isOpen={showBreakModal}
        onClose={() => setShowBreakModal(false)}
        title=""
        size="md"
      >
        <div className="text-center py-6 px-4">
          {/* Break Icon */}
          <div className="mx-auto inline-flex items-center justify-center h-20 w-20 rounded-full bg-blue-100 mb-5">
            <svg className="h-10 w-10 text-blue-600" fill="none" stroke="currentColor" viewBox="0 0 24 24" strokeWidth={2}>
              <path strokeLinecap="round" strokeLinejoin="round" d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" />
            </svg>
          </div>

          {/* Title */}
          <h3 className="text-2xl font-bold text-neutral-900 mb-3">
            Time for a Break!
          </h3>

          {/* Section Name */}
          <p className="text-base text-neutral-600 mb-2">
            "{breakInfo.name}" has ended
          </p>

          {/* Break Duration */}
          <div className="bg-blue-50 rounded-xl p-6 mb-6">
            <p className="text-sm text-blue-600 font-medium mb-2">Break Duration</p>
            <p className="text-4xl font-bold text-blue-700">
              {breakInfo.breakTime} <span className="text-xl font-normal">min</span>
            </p>
          </div>

          {/* Message */}
          <p className="text-sm text-neutral-500 mb-6">
            Take a moment to stretch, grab a drink, or relax. The session will resume shortly.
          </p>

          {/* OK Button */}
          <Button
            variant="primary"
            onClick={() => setShowBreakModal(false)}
            fullWidth
            size="lg"
          >
            Got it!
          </Button>
        </div>
      </Modal>

      {/* Subsession Started Modal (Break Ended) */}
      <Modal
        isOpen={showSubsessionStartedModal}
        onClose={() => setShowSubsessionStartedModal(false)}
        title=""
        size="md"
      >
        <div className="text-center py-6 px-4">
          <div className="mx-auto inline-flex items-center justify-center h-20 w-20 rounded-full bg-green-100 mb-5">
            <svg className="h-10 w-10 text-green-600" fill="none" stroke="currentColor" viewBox="0 0 24 24" strokeWidth={2}>
              <path strokeLinecap="round" strokeLinejoin="round" d="M14.752 11.168l-3.197-2.132A1 1 0 0010 9.87v4.263a1 1 0 001.555.832l3.197-2.132a1 1 0 000-1.664z" />
              <path strokeLinecap="round" strokeLinejoin="round" d="M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
            </svg>
          </div>
          <h3 className="text-2xl font-bold text-neutral-900 mb-3">Session Resuming!</h3>
          <p className="text-base text-neutral-600 mb-2">
            {subsessionInfo.number > 0 ? `Section ${subsessionInfo.number}` : 'Next section'} is starting
          </p>
          <div className="bg-green-50 rounded-xl p-6 mb-6">
            <p className="text-sm text-green-600 font-medium mb-2">Now Starting</p>
            <p className="text-xl font-bold text-green-700">"{subsessionInfo.name}"</p>
          </div>
          <p className="text-sm text-neutral-500 mb-6">
            Get ready! New activities may be launched soon.
          </p>
          <Button variant="primary" onClick={() => setShowSubsessionStartedModal(false)} fullWidth size="lg">
            I'm Ready!
          </Button>
        </div>
      </Modal>

      {/* Session Ended Modal */}
      <Modal
        isOpen={showSessionEndedModal}
        onClose={() => {
          setShowSessionEndedModal(false);
          navigate(`/session/${sessionId}/reports`, { replace: true });
        }}
        title=""
        size="md"
      >
        <div className="text-center py-6 px-4">
          <div className="mx-auto inline-flex items-center justify-center h-20 w-20 rounded-full bg-orange-100 mb-5">
            <svg className="h-10 w-10 text-orange-600" fill="none" stroke="currentColor" viewBox="0 0 24 24" strokeWidth={2}>
              <path strokeLinecap="round" strokeLinejoin="round" d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" />
            </svg>
          </div>
          <h3 className="text-2xl font-bold text-neutral-900 mb-3">Session Ended</h3>
          <p className="text-base text-neutral-600 mb-6 leading-relaxed">
            {sessionEndReason || 'The trainer has ended this session. Thank you for participating!'}
          </p>
          {session && (
            <div className="bg-neutral-50 rounded-lg p-4 mb-6">
              <p className="text-sm font-semibold text-neutral-900">{session.name}</p>
              <p className="text-xs text-neutral-500 mt-1">Session Code: {session.code}</p>
            </div>
          )}
          <div className="flex flex-col gap-3">
            <Button
              variant="primary"
              onClick={() => {
                setShowSessionEndedModal(false);
                navigate(`/session/${sessionId}/reports`);
              }}
              fullWidth
              size="lg"
            >
              View My Report
            </Button>
            <Button
              variant="ghost"
              onClick={() => {
                setShowSessionEndedModal(false);
                navigate('/join', { replace: true });
              }}
              fullWidth
            >
              Join Another Session
            </Button>
          </div>
        </div>
      </Modal>

      {/* Session Paused Modal - For Multi-Day Sessions */}
      <Modal
        isOpen={showSessionPausedModal}
        onClose={() => setShowSessionPausedModal(false)}
        title=""
        size="md"
      >
        <div className="text-center py-6 px-4">
          <div className="mx-auto inline-flex items-center justify-center h-20 w-20 rounded-full bg-purple-100 mb-5">
            <svg className="h-10 w-10 text-purple-600" fill="currentColor" viewBox="0 0 24 24">
              <path d="M6 4h4v16H6V4zm8 0h4v16h-4V4z" />
            </svg>
          </div>
          <h3 className="text-2xl font-bold text-neutral-900 mb-3">Session Paused for Today</h3>
          <p className="text-base text-neutral-600 mb-4 leading-relaxed">
            The session has been paused and will continue tomorrow. Your progress has been saved!
          </p>

          {/* Participant Code Display */}
          {participant?.participantCode && (
            <div className="bg-purple-50 border-2 border-purple-200 rounded-xl p-4 mb-4">
              <p className="text-xs text-purple-600 font-semibold mb-2">Save Your Participant Code</p>
              <div className="flex items-center justify-center gap-3">
                <span className="text-2xl font-bold text-purple-700 tracking-wider">
                  {participant.participantCode}
                </span>
                <button
                  onClick={async () => {
                    try {
                      await navigator.clipboard.writeText(participant.participantCode);
                      setPausedCodeCopied(true);
                      setTimeout(() => setPausedCodeCopied(false), 2000);
                    } catch (err) {
                      console.error('Failed to copy:', err);
                    }
                  }}
                  className={`p-2 rounded-lg transition-colors flex items-center gap-1 ${
                    pausedCodeCopied
                      ? 'bg-green-100 text-green-600'
                      : 'text-purple-600 hover:bg-purple-100'
                  }`}
                  title={pausedCodeCopied ? 'Copied!' : 'Copy to clipboard'}
                >
                  {pausedCodeCopied ? (
                    <>
                      <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                      </svg>
                      <span className="text-xs font-medium">Copied</span>
                    </>
                  ) : (
                    <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 16H6a2 2 0 01-2-2V6a2 2 0 012-2h8a2 2 0 012 2v2m-6 12h8a2 2 0 002-2v-8a2 2 0 00-2-2h-8a2 2 0 00-2 2v8a2 2 0 002 2z" />
                    </svg>
                  )}
                </button>
              </div>
              <p className="text-xs text-purple-600 mt-2">
                Use this code along with session code <span className="font-bold">{session?.code}</span> to rejoin tomorrow
              </p>
            </div>
          )}

          {/* Reminder */}
          <div className="bg-amber-50 border border-amber-200 rounded-lg p-3 mb-6">
            <div className="flex items-start gap-2 text-left">
              <svg className="w-4 h-4 text-amber-600 mt-0.5 flex-shrink-0" fill="currentColor" viewBox="0 0 20 20">
                <path fillRule="evenodd" d="M8.257 3.099c.765-1.36 2.722-1.36 3.486 0l5.58 9.92c.75 1.334-.213 2.98-1.742 2.98H4.42c-1.53 0-2.493-1.646-1.743-2.98l5.58-9.92zM11 13a1 1 0 11-2 0 1 1 0 012 0zm-1-8a1 1 0 00-1 1v3a1 1 0 002 0V6a1 1 0 00-1-1z" clipRule="evenodd" />
              </svg>
              <div>
                <p className="text-xs font-medium text-amber-800">Remember to save your code!</p>
                <p className="text-xs text-amber-700 mt-0.5">
                  Take a screenshot or note it down. You'll need it to rejoin tomorrow where you left off.
                </p>
              </div>
            </div>
          </div>

          <div className="flex flex-col gap-3">
            <Button
              variant="primary"
              onClick={() => {
                setShowSessionPausedModal(false);
                navigate(`/session/${sessionId}/reports`);
              }}
              fullWidth
              size="lg"
            >
              View My Progress
            </Button>
            <Button
              variant="secondary"
              onClick={() => {
                setShowSessionPausedModal(false);
                setActiveNavItem('content');
              }}
              fullWidth
            >
              Browse Content & Workbooks
            </Button>
            <Button
              variant="ghost"
              onClick={() => setShowSessionPausedModal(false)}
              fullWidth
            >
              Stay in Session
            </Button>
          </div>
        </div>
      </Modal>

      {/* Locked Phase Popup — shown when a participant clicks Session/Post-Session
          before that phase is available. */}
      <Modal
        isOpen={lockedDialog.open}
        onClose={() => setLockedDialog({ open: false, title: '', message: '' })}
        size="sm"
        showCloseButton={false}
      >
        <div className="text-center py-2">
          <div className="w-16 h-16 bg-amber-100 rounded-full flex items-center justify-center mx-auto mb-4">
            <svg className="w-8 h-8 text-amber-600" fill="none" stroke="currentColor" viewBox="0 0 24 24" strokeWidth={1.8}>
              <path strokeLinecap="round" strokeLinejoin="round" d="M16.5 10.5V6.75a4.5 4.5 0 10-9 0v3.75m-.75 11.25h10.5a2.25 2.25 0 002.25-2.25v-6.75a2.25 2.25 0 00-2.25-2.25H6.75a2.25 2.25 0 00-2.25 2.25v6.75a2.25 2.25 0 002.25 2.25z" />
            </svg>
          </div>
          <h3 className="text-lg font-semibold text-neutral-900 mb-2">{lockedDialog.title}</h3>
          <p className="text-sm text-neutral-600 mb-6">{lockedDialog.message}</p>
          <Button
            onClick={() => setLockedDialog({ open: false, title: '', message: '' })}
            fullWidth
          >
            Got it
          </Button>
        </div>
      </Modal>
    </div>
  );
};

export default SessionLobbyPage;
